import { Circuitry, Warning, Info, Code, Link as LinkIcon } from "@phosphor-icons/react";

const CodeBlock = ({ children, testId }) => (
  <pre data-testid={testId} className="bg-[#0B1220] text-[#D1D5DB] text-xs leading-relaxed rounded-lg p-4 overflow-x-auto font-data whitespace-pre">
    {children}
  </pre>
);

const Section = ({ number, title, children, testId }) => (
  <section className="mb-10" data-testid={testId}>
    <div className="flex items-center gap-3 mb-3">
      <div className="w-8 h-8 rounded-full bg-[#004B87] text-white font-heading font-bold text-sm flex items-center justify-center shrink-0">
        {number}
      </div>
      <h2 className="font-heading text-lg font-bold text-[#101828]">{title}</h2>
    </div>
    <div className="pl-11 text-sm text-[#475569] leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function SapInternalCommentDocsPage() {
  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="sap-internal-comment-docs-page">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center px-4 sm:px-6 shrink-0 z-10">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-white/15 flex items-center justify-center">
            <Circuitry size={18} className="text-white" weight="fill" />
          </div>
          <span className="font-heading text-[16px] font-bold text-white tracking-tight">SAP Integration Reference &middot; Internal Comment / Reference ID</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-10">
          <p className="font-data text-xs font-bold text-[#004B87] uppercase tracking-widest mb-2" data-testid="doc-code">SOP-SAP-GM-01 &middot; Goods Movement extension field</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#101828] tracking-tight" data-testid="page-title">
            Writing a Reference ID into SAP's own Goods Movement record
          </h1>
          <p className="text-base text-[#475569] mt-3 max-w-2xl leading-relaxed">
            This app stamps every Goods Movement it posts (STO relocation, HOLD &rarr; Destination; GRN movement,
            HOLD &rarr; RM) with its own reference ID and the requesting user's name, written directly into a
            field on SAP's own confirmation document. Hand this page to any other app's team that needs the same
            thing - it is <strong>not</strong> a standard SAP field, so three things have to line up.
          </p>
        </div>

        <div className="flex items-start gap-3 bg-[#FFFAEB] border border-[#FEDF89] rounded-lg p-4 mb-10" data-testid="warning-banner">
          <Warning size={20} className="text-[#B54708] shrink-0 mt-0.5" weight="fill" />
          <p className="text-sm text-[#B54708] leading-relaxed">
            SAP silently ignores this extension element if it isn't ticked for the exact operation you're calling -
            no error, the box just stays empty. And confirmation IDs are <strong>not</strong> issued in time order
            (one can come after a numerically earlier one), so a lost response can't be recovered by assuming "the
            next number" - read it back by scanning a range instead.
          </p>
        </div>

        <Section number={1} title="The field" testId="section-field">
          <p><strong>Name in SAP:</strong> Remarks for Issue &middot; <strong>technical name:</strong> <code className="bg-[#F2F4F7] px-1.5 py-0.5 rounded text-xs">RemarkforIssue</code></p>
          <p><strong>Business context:</strong> Goods and Activity Confirmation &ndash; General Information (header-level, not per-line).</p>
          <p><strong>Namespace:</strong> <code className="bg-[#F2F4F7] px-1.5 py-0.5 rounded text-xs break-all">http://sap.com/xi/AP/CustomerExtension/BYD/A4CF6</code> &mdash; this is tenant-specific (printed on your own tenant's Further Usage screen); another tenant will have a different suffix than <code className="bg-[#F2F4F7] px-1 rounded text-xs">A4CF6</code>.</p>
          <p><strong>Limit:</strong> 120 characters, XML-escape the text.</p>
        </Section>

        <Section number={2} title="The two SAP ticks (confirm both exist in your tenant)" testId="section-ticks">
          <ul className="list-disc pl-5 space-y-1.5">
            <li><em>Further Usage of Extension Field &rarr; Services</em> &mdash; tick the operation the field will arrive through (e.g. <code className="bg-[#F2F4F7] px-1 rounded text-xs">DoGoodsMovement</code>).</li>
            <li>Open the document &rarr; <em>Adapt &rarr; Edit Screen</em> &rarr; set the field <strong>Visible</strong> &rarr; Save &rarr; Publish.</li>
          </ul>
        </Section>

        <Section number={3} title="The XML" testId="section-xml">
          <p>The element goes inside <code className="bg-[#F2F4F7] px-1 rounded text-xs">&lt;GoodsAndActivityConfirmation&gt;</code>, <strong>after</strong> the item block(s):</p>
          <CodeBlock testId="xml-code-block">{`<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/"
                  xmlns:glob="http://sap.com/xi/SAPGlobal20/Global"
                  xmlns:ext="http://sap.com/xi/AP/CustomerExtension/BYD/A4CF6">
 <soapenv:Body>
  <glob:GoodsAndActivityConfirmationGoodsMovement>
   <GoodsAndActivityConfirmation>
    <ExternalID>...</ExternalID><SiteID>P1</SiteID><TransactionDateTime>...</TransactionDateTime>
    <InventoryChangeItemGoodsMovement>...</InventoryChangeItemGoodsMovement>
    <ext:RemarkforIssue>STO STO-001281 | Requester Ankit Sharma</ext:RemarkforIssue>
   </GoodsAndActivityConfirmation>
  </glob:GoodsAndActivityConfirmationGoodsMovement>
 </soapenv:Body>
</soapenv:Envelope>`}</CodeBlock>
          <p>This app's own reference format: <code className="bg-[#F2F4F7] px-1.5 py-0.5 rounded text-xs">STO &lt;sto_id&gt; | Requester &lt;name&gt;</code> for inbound STO relocations, and <code className="bg-[#F2F4F7] px-1.5 py-0.5 rounded text-xs">GRN &lt;shipment_code&gt; | Requester &lt;approver name&gt;</code> for supplier GRN movements.</p>
        </Section>

        <Section number={4} title="Endpoints and auth" testId="section-endpoints">
          <p>Basic auth, the communication-arrangement user &mdash; a new app should get its own user and arrangement rather than share this app's technical user.</p>
          <ul className="space-y-2 mt-2">
            <li className="flex items-start gap-2"><LinkIcon size={14} className="mt-1 shrink-0 text-[#667085]" /><span><strong>Transfers:</strong> <code className="bg-[#F2F4F7] px-1 rounded text-xs break-all">/sap/bc/srt/scs/sap/inventoryprocessinggoodsandac2</code> &middot; SOAPAction <code className="bg-[#F2F4F7] px-1 rounded text-xs break-all">.../InventoryProcessingGoodsAndActivityConfirmationGoodsMovementIn/DoGoodsMovement</code></span></li>
            <li className="flex items-start gap-2"><LinkIcon size={14} className="mt-1 shrink-0 text-[#667085]" /><span><strong>Consumption (cost centre / project):</strong> <code className="bg-[#F2F4F7] px-1 rounded text-xs break-all">/sap/bc/srt/scs/sap/inventoryprocessinggoodsandac3</code></span></li>
            <li className="flex items-start gap-2"><Code size={14} className="mt-1 shrink-0 text-[#667085]" /><span><strong>Read it back to prove it landed:</strong> <code className="bg-[#F2F4F7] px-1 rounded text-xs break-all">/sap/bc/srt/scs/sap/querygoodsandactivityconfirma1</code> &rarr; <code className="bg-[#F2F4F7] px-1 rounded text-xs break-all">FindInventoryChangeItemOverview</code> returns <code className="bg-[#F2F4F7] px-1 rounded text-xs break-all">RemarkforIssue</code>.</span></li>
          </ul>
        </Section>

        <div className="flex items-start gap-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-lg p-4" data-testid="info-banner">
          <Info size={20} className="text-[#1E40AF] shrink-0 mt-0.5" weight="fill" />
          <p className="text-sm text-[#1E40AF] leading-relaxed">
            This app's own implementation lives in <code className="bg-white/60 px-1 rounded text-xs break-all">sap_goods_movement_client.py</code>
            (<code className="bg-white/60 px-1 rounded text-xs break-all">_build_remark_element</code>), wired into both STO inbound relocation
            and supplier GRN movements with the requesting user's name included automatically.
          </p>
        </div>

        <div className="mt-12 border-t border-[#EAECF0] pt-6 text-center">
          <p className="text-xs text-[#94A3B8] font-data">SAP Integration Reference &middot; Internal Comment / Reference ID on Goods Movement</p>
        </div>
      </main>
    </div>
  );
}
